import { useTranslation } from "react-i18next";

interface PipelineNode {
  title: string;
  caption1: string;
  caption2: string;
}

const NODE_NUMBERS = ["01", "02", "03", "04"];
const NODE_W = 200;
const NODE_H = 132;
const Y = 110;
const CENTERS = [130, 380, 630, 880];

export function DubPipelineDiagram() {
  const { t } = useTranslation();
  const nodes = t("work.dubcircus.pipeline.nodes", {
    returnObjects: true,
  }) as PipelineNode[];

  return (
    <div className="-mx-6 overflow-x-auto sm:mx-0">
      <div className="min-w-[1020px] px-6 sm:min-w-0 sm:px-0">
        <svg
          viewBox="0 0 1010 220"
          aria-hidden="true"
          className="h-auto w-full"
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <marker
              id="dub-arrow"
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--color-accent)" />
            </marker>
          </defs>

          {/* Connectors */}
          {[0, 1, 2].map((i) => {
            const x1 = CENTERS[i]! + NODE_W / 2 + 6;
            const x2 = CENTERS[i + 1]! - NODE_W / 2 - 12;
            return (
              <line
                key={i}
                x1={x1}
                y1={Y}
                x2={x2}
                y2={Y}
                stroke="var(--color-ink-3)"
                strokeWidth="1.4"
                strokeDasharray="4 5"
                markerEnd="url(#dub-arrow)"
              />
            );
          })}

          {/* Nodes */}
          {nodes.map((node, i) => {
            const cx = CENTERS[i]!;
            const x = cx - NODE_W / 2;
            const y = Y - NODE_H / 2;
            const n = NODE_NUMBERS[i]!;
            return (
              <g key={n}>
                <rect
                  x={x}
                  y={y}
                  width={NODE_W}
                  height={NODE_H}
                  rx="14"
                  fill="var(--color-surface)"
                  stroke="var(--color-line-strong)"
                  strokeWidth="1.2"
                />
                <text
                  x={x + 18}
                  y={y + 28}
                  fontFamily="var(--font-mono)"
                  fontSize="11"
                  letterSpacing="1.6"
                  fill="var(--color-accent)"
                >
                  {n}
                </text>
                <text
                  x={x + 18}
                  y={y + 60}
                  fontFamily="var(--font-sans)"
                  fontSize="20"
                  fontWeight="500"
                  fill="var(--color-ink)"
                  letterSpacing="-0.4"
                >
                  {node.title}
                </text>
                <text
                  x={x + 18}
                  y={y + 86}
                  fontFamily="var(--font-sans)"
                  fontSize="12"
                  fill="var(--color-ink-2)"
                >
                  {node.caption1}
                </text>
                <text
                  x={x + 18}
                  y={y + 104}
                  fontFamily="var(--font-sans)"
                  fontSize="12"
                  fill="var(--color-ink-2)"
                >
                  {node.caption2}
                </text>
                {i === nodes.length - 1 && (
                  <line
                    x1={x + 18}
                    y1={y + NODE_H - 12}
                    x2={x + 64}
                    y2={y + NODE_H - 12}
                    stroke="var(--color-accent)"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                )}
              </g>
            );
          })}
        </svg>
      </div>
      <p className="sr-only">{t("work.dubcircus.pipeline.srDescription")}</p>
    </div>
  );
}
